/**
 * 予約の getters
 */
import deepmerge from 'deepmerge'
import * as C from '@/consts'
import * as api from '@/api'
import * as http from '@/libs/http'
import Calendar from './calendar'
import * as time from '@/libs/time'

export default {
  /**
   * 初期化されているか
   *
   * @param {State} state
   * @returns {Boolean} 処理化されていたら true
   */
  initialized (state) {
    return state.initialized
  },

  /**
   * 予約 ID を返す
   *
   * @param {State} state
   * @returns {Number} 予約 ID を返す
   */
  reserveID (state) {
    return state.reserveID
  },

  /**
   * ステーション情報 を返す
   *
   * @param {State} state
   * @returns {String} ステーション情報 を返す
   */
  station (state) {
    return deepmerge(state.station, {})
  },

  /**
   * ステーションID を返す
   *
   * @param {State} state
   * @returns {Number} ステーションID を返す
   */
  stationID (state) {
    return state.station.stationID
  },

  /**
   * 店舗ID を返す
   *
   * @param {State} state
   * @returns {Number} 店舗ID を返す
   */
  shopID (state) {
    return state.station.shopID
  },

  /**
   * カレンダーオブジェクトを返す
   *
   * @param {State} state
   * @param {Getters} getters
   * @param {State} rootState
   * @param {Getters} rootGetters
   * @returns {async Calendar} カレンダーオブジェクトを返す
   */
  calendar (state, getters, rootState, rootGetters) {
    return async function () {
      const orgcode = rootGetters['org/code']

      // api から必要な情報を取得する
      const results = await Promise.all([
        // ステーションのメタ情報を取得する
        api.getStation(orgcode, state.station.stationID, [
          'meta'
        ]),

        // カレンダーを取得する
        api.getShopCalendar(state.station.shopID)
      ])
      // const meta = results[0].data.meta
      const calendar = results[1].data

      // 車両カテゴリ, 24H サポートを取得する
      let category, anytime

      if (state.choosedCar && state.car) {
        // 初期化時に車両を選択していれば、車両情報を使用する
        category = state.car.category
        if (state.car.attributes.keykey === 1 || state.car.attributes.vkey === 1) {
          anytime = 1
        } else {
          anytime = 0
        }
      } else {
        // ステーション情報を使用する
        category = '*'
        // 車両を選択していなければ24H選択は不可
        anytime = 0
      }

      // ユーザが keykey を利用できるかを anytime に反映する
      if (state.car.attributes.keykey === 1 || state.car.attributes.vkey === 1) {
      } else {
        anytime &&= rootGetters['user/keykey']
      }

      // ユーザーが初回利用の場合は24時間不可にする
      const today = time.today()
      const firstTime = rootGetters['user/limited'](today).firstTime
      console.log(firstTime)
      if (firstTime) {
        anytime = 0
      }

      // カテゴリ設定を得る
      let settings = calendar.categories.find(item => item.category === category)
      if (!settings) {
        settings = calendar.categories[0]
      }

      // カレンダーオブジェクトを返す
      return new Calendar({
        daybreak: settings.daybreak,
        span: settings.span,
        anytime,
        allow: state.allow || settings.allow,
        dates: calendar.dates
      })
    }
  },

  /**
   * 予約期間を返す
   *
   * @param {State} state
   * @returns {Object?} 予約期間を返す
   */
  schedule (state) {
    return state.schedule
  },

  /**
   * 初期化時に車両を選択しているか
   *
   * @param {State} state
   * @returns {Boolean} 初期化時に車両を選択していれば true を返す
   */
  choosedCar (state) {
    return state.choosedCar
  },

  /**
   * 車両情報を返す
   *
   * @param {State} state
   * @returns {Object?} 車両情報を返す
   */
  car (state) {
    return state.car ? deepmerge(state.car, {}) : null
  },

  /**
   * オプション情報を返す
   *
   * @param {State} state
   * @returns {Array} オプション情報を返す
   */
  options (state) {
    // 計上日数
    const optDays = state.period ? state.period.optDays : 0

    return state.options.map(item => {
      // 料金を算出する
      let days
      if (item.chargeType === C.OPTION_CHARGE_TIME) {
        days = 1
      } else if (optDays < item.maxDays) {
        days = optDays
      } else {
        days = item.maxDays
      }
      const charge = item.quantity * item.unitCharge * days

      // 保存情報に料金を付加する
      return { ...item, charge }
    })
  },

  /**
   * 免責補償情報を返す
   *
   * @param {State} state
   * @returns {Object} 免責補償情報を返す
   */
  insurance (state) {
    const insurance = state.insurance

    // 計上日数
    const optDays = state.period ? state.period.insuranceDays : 0

    let days
    if (optDays < insurance.maxDays) {
      days = optDays
    } else {
      days = insurance.maxDays
    }
    const charge = insurance.unitCharge * days

    return { ...insurance, charge }
  },

  /**
   * 送迎情報を返す
   *
   * @param {State} state
   * @returns {Object} 送迎情報を返す
   */
  pick (state) {
    const pick = state.pick

    let name, charge
    switch (pick.value) {
      case C.WAY_START:
        name = '出発のみ'
        charge = pick.oneway
        break
      case C.WAY_RETURN:
        name = '帰着のみ'
        charge = pick.oneway
        break
      case C.WAY_ROUND:
        name = '往復'
        charge = pick.round
        break
      default:
        name = 'なし'
        charge = 0
        break
    }
    return { ...pick, name, charge }
  },

  /**
   * 配車情報を返す
   *
   * @param {State} state
   * @returns {Object} 配車情報を返す
   */
  deli (state) {
    const deli = state.deli

    let name, charge
    switch (deli.value) {
      case C.WAY_START:
        name = '出発のみ'
        charge = deli.oneway
        break
      case C.WAY_RETURN:
        name = '帰着のみ'
        charge = deli.oneway
        break
      case C.WAY_ROUND:
        name = '往復'
        charge = deli.round
        break
      default:
        name = 'なし'
        charge = 0
        break
    }
    return { ...deli, name, charge }
  },

  /**
   * 乗り捨て情報を返す
   *
   * @param {State} state
   * @returns {Object} 乗り捨て情報を返す
   */
  returns (state) {
    const returns = deepmerge(state.returns, {})
    const charge = returns.station ? returns.station.charge : 0
    return { ...returns, charge }
  },

  /**
   * フライト情報を返す
   *
   * @param {State} state
   * @returns {Object} フライト情報を返す
   */
  flights (state) {
    return deepmerge(state.flights, {})
  },

  /**
   * 乗車人数を返す
   *
   * @param {State} state
   * @returns {Number} 乗車人数を返す
   */
  peoples (state) {
    return state.peoples
  },

  /**
   * クーポンを返す
   *
   * @param {State} state
   * @returns {Object?} クーポンを返す
   */
  coupon (state) {
    return state.coupon ? deepmerge(state.coupon, {}) : null
  },

  /**
   * 連絡事項を返す
   *
   * @param {State} state
   * @returns {String} 連絡事項を返す
   */
  note (state) {
    return state.note
  },

  /**
   * 支払方法を返す
   *
   * @param {State} state
   * @returns {Object?} 支払方法を返す
   */
  pay (state) {
    return state.pay ? deepmerge(state.pay, {}) : null
  },

  /**
   * 入力用のオプション情報を返す
   *
   * @param {State} state
   * @returns {async Object} 入力用のオプション情報を返す
   */
  getOptions (state) {
    return async function () {
      let options

      if (!state.period) {
        throw new Error('missing data for options: Expected "period" to be defined.')
      }

      // オプション一覧を取得する
      const resp = await api.getShopOptions(state.station.shopID)
      if (resp.status === http.OK) {
        options = resp.data || []
      } else {
        options = []
      }

      // 車両カテゴリに一致するもののみにする
      const category = state.car.category
      options = options.filter(item => !item.categories || item.categories.includes(category))

      // 車両に応じてスタッドレスを追加する
      if (state.car.attributes.studless) {
        options.push({
          id: C.OPTION_STUDLESS_ID,
          categories: [category],
          type: C.OPTION_STUDLESS,
          name: 'スタッドレスタイヤ',
          image: null,
          desc: '',
          max: 1,
          cap: 999,
          unit: '',
          chargeType: C.OPTION_CHARGE_TIME,
          maxDays: 999,
          unitCharge: state.period.studlessCharge
        })
      }

      // 入力値を含める
      options = options.map(opt => {
        // 入力値 (数量) を得る
        const input = state.options.find(item => item.id === opt.id)
        const quantity = input ? input.quantity : 0

        // オプションマスタ情報に quantity を付加する
        return {
          ...opt,
          quantity
        }
      })

      return options
    }
  },

  /**
   * 入力用の免責補償情報を返す
   *
   * @param {State} state
   * @param {Getters} getters
   * @param {State} rootState
   * @param {Getters} rootGetters
   * @returns {async Object} 入力用の免責補償情報を返す
   */
  getInsurance (state, getters, rootState, rootGetters) {
    return async function () {
      let value

      if (!state.period) {
        throw new Error('missing data for options: Expected "period" to be defined.')
      }

      // 免責補償一覧を取得する
      const resp = await api.getShopInsurances(state.station.shopID)
      let { desc, items } = resp.data
      if (!desc) {
        desc = ''
      }
      if (!items) {
        items = []
      }

      // ユーザ制約
      const user = rootGetters['user/limited'](state.schedule.begin)
      const limited = (user.beginner || user.minor) ? C.USER_BEGINNER : C.USER_EXPERT

      // 車両カテゴリやユーザ制約から選択できるもののみにする
      const category = state.car.category
      items = items.filter(item => {
        // カテゴリが一致しないものは除外する
        if (item.categories.length > 0 && !item.categories.includes(category) && category) {
          return false
        }
        // ユーザ制約に一致しないものは除外する
        if (item.limited !== 0 && item.limited !== limited) {
          return false
        }
        return true
      })

      // items が空でなければ なし を追加する
      if (items.length > 0) {
        items.splice(0, 0, {
          id: 0,
          categories: [category],
          name: 'なし',
          default: false,
          unitCharge: 0,
          limited: limited,
          maxDays: Infinity
        })
      }

      // 保存されている入力値が items にあるか
      if (state.insurance.status === C.INPUT_RESOLVED) {
        if (state.insurance.id === 0) {
          // なしを選択している
          value = 0
        } else if (items.findIndex(item => item.id === state.insurance.id) >= 0) {
          // items にあるものを選択している
          value = state.insurance.id
        }
      }
      // デフォルトの要素を選択する
      if (value === undefined) {
        const defaultItem = items.find(item => item.default)
        value = defaultItem ? defaultItem.id : 0
      }

      return {
        desc,
        items,
        value
      }
    }
  },

  /**
   * 入力用のサービス情報を返す
   *
   * @param {State} state
   * @returns {async Object} 入力用のサービス情報を返す
   */
  getServices (state) {
    return async function () {
      // サービス情報を取得する
      const resp = await api.getShopServices(state.station.shopID)
      const services = deepmerge({
        pick: {
          enabled: false,
          desc: '',
          places: [],
          any: false,
          oneway: 0,
          round: 0
        },
        deli: {
          enabled: false,
          desc: '',
          places: [],
          any: false,
          oneway: 0,
          round: 0
        },
        returns: {
          enabled: false,
          desc: '',
          stations: []
        },
        flights: {
          enabled: false,
          desc: ''
        }
      }, resp.data)

      // 送迎
      const pickInput = services.pick.enabled
        ? state.pick
        : { value: C.WAY_NONE, place: '' }

      //
      if (services.pick.places.length === 1) {
        pickInput.place = services.pick.places[0]
      }

      const pick = {
        // マスタ情報
        ...services.pick,

        // 入力値
        value: pickInput.value,
        place: pickInput.place
      }

      // 配車
      const deliInput = services.deli.enabled
        ? state.deli
        : { value: C.WAY_NONE, place: '' }

      const deli = {
        // マスタ情報
        ...services.deli,

        // 入力値
        value: deliInput.value,
        place: deliInput.place
      }

      // 乗り捨て
      const returnsInput = services.returns.enabled
        ? state.returns
        : { station: null }

      const returns = {
        // マスタ情報
        ...services.returns,

        // 入力値
        stationID: returnsInput.station ? returnsInput.station.stationID : 0
      }

      // フライト
      const flightInput = services.flights.enabled
        ? state.flights
        : { decided: false, arrival: '', departure: '' }

      const flights = {
        // マスタ情報
        ...services.flights,

        // 入力値
        decided: flightInput.decided,
        arrival: flightInput.arrival,
        departure: flightInput.departure
      }

      return {
        pick,
        deli,
        returns,
        flights
      }
    }
  },

  /**
   * 料金情報を返す
   *
   * @param {State} state
   */
  charge (state, getters) {
    // 基本料金
    const basic = state.period ? state.period.charge : 0

    // 追加料金
    let additional = getters.options.reduce((sum, item) => sum + item.charge, 0)
    additional += getters.insurance.charge
    additional += getters.pick.charge
    additional += getters.deli.charge
    additional += getters.returns.charge

    // 値引き
    let discount = 0
    if (getters.coupon) {
      discount = getters.coupon.value
    }

    return {
      basic,
      additional,
      discount,
      total: basic + additional - discount
    }
  },

  /**
   * 変更前の料金情報を返す
   *
   * @param {State} state
   * @returns {Object} charge 料金情報
   */
  beforeCharge (state) {
    return state.charge
  }
}
